<template>
	<v-layout justify-center class="login-page">
		<v-flex xs12 sm8 md4 class="wrapper">
			<img class="app-logo" :src="displayedLogoUrl">

			<v-card>
				<v-card-text class="card-content" v-if="!loading">
					<v-form id="loginForm" @submit.prevent>
						<v-text-field
							v-if="step === 1"
							label="Username or Email"
							type="text"
							v-model="email"
							outlined
							:error-messages="emailErrors"
							@keyup.enter.native="goToPasswordStep"
							ref="email"
							id="username"
						></v-text-field>

						<template v-else>
							<div class="username-display">
								<span class="username-display-text" id="username">{{ email }}</span>
								<a class="username-edit" @click="editUsername">Edit</a>
							</div>

							<v-text-field
								label="Password"
								type="password"
								v-model="password"
								outlined
								:error-messages="passwordErrors"
								@keyup.enter.native="signIn"
								@keydown.native="checkCapsLock"
								ref="password"
								id="password"
							></v-text-field>
						</template>
					</v-form>

					<v-btn
						v-if="step === 1"
						color="primary"
						@click="goToPasswordStep"
						id="nextButton"
						rounded
					>Next</v-btn>
					<v-btn
						v-else
						color="primary"
						@click="signIn"
						id="loginButton"
						rounded
					>Log In</v-btn>

					<div v-if="capsLockMessage" class="error-message">{{ capsLockMessage }}</div>
					<div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
				</v-card-text>

				<v-progress-circular class="loading" indeterminate v-if="loading"></v-progress-circular>
			</v-card>

			<v-btn text to="/reset-password" class="forgot-password-button">Forgot Password</v-btn>
		</v-flex>

		<AccountPickerDialog :open="accountPickerDialog" :plic-slug="plicSlug" :redirect-url="redirectUrl" @error="onError" />
	</v-layout>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import { client, plicAxios, setupAxiosAuthToken } from '../utils/axios';
import { sanitizeRedirect } from '../utils/sanitize-redirect';
import AccountPickerDialog from './AccountPickerDialog.vue';

export default {
	props: {
		'emailOnly': {
			default: true
		}
	},
	name: 'LandingPage',
	data() {
		return {
			step: 1,
			email: '',
			password: '',
			loading: false,
			errorMessage: null,
			successMessage: null,
			capsLockMessage: null,
			accountPickerDialog: false,
			plicSlug: null,
			redirectUrl: null
		};
	},
	validations() {
		const emailValidations = { required };
		if(this.emailOnly) {
			emailValidations.email = email;
		}

		return {
			email: emailValidations,
			password: { required }
		};
	},
	computed: {
		emailErrors() {
			const errors = [];
			if(this.$v.email.$dirty) {
				this.$v.email.required || errors.push('Username is required');
				if(this.emailOnly) {
					this.$v.email.email || errors.push('Username is required to be a valid email');
				}
			}

			return errors;
		},
		passwordErrors() {
			const errors = [];
			if(this.$v.password.$dirty) {
				this.$v.password.required || errors.push('Password is required');
			}

			return errors;
		},

		displayedLogoUrl() {
			return this.lightLargeLogoUrl;
		}
	},
	methods: {
		async goToPasswordStep() {
			this.errorMessage = null;
			this.$v.email.$touch();

			if(this.$v.email.$invalid) {
				return;
			}

			this.loading = true;
			if(await this.handleLoginIntent()) {
				// Leave the spinner up while the browser navigates away
				return;
			}
			this.loading = false;

			this.step = 2;
			this.$nextTick(() => {
				const passwordField = this.$refs.password;
				if(passwordField) {
					passwordField.$el.querySelector('input').focus();
				}
			});
		},
		// Asks PLIC what kind of login this email requires before we show a password
		// field. Returns true when we are taking over navigation, false to continue
		// on to the password step.
		//
		// Users opted in to enhanced sign-in have to complete Auth0 + MFA before PLIC
		// will issue them a token, so they must never be shown a password field.
		// Anything we cannot interpret - a network failure, an unknown status, a PLIC
		// that does not have this endpoint yet - falls through to the password step.
		// That keeps this deployable independently of PLIC, and it stays safe because
		// PLIC itself rejects the password grant for those users.
		async handleLoginIntent() {
			// Books allows usernames that are not email addresses, and PLIC rejects
			// those with a 422, so only ask about values that could be a PLIC user
			if(!email(this.email)) {
				return false;
			}

			try {
				const response = await plicAxios.post('login-intents/lookup', {
					email: this.email,
					plic_app_id: process.env.VUE_APP_PLIC_APP_SLUG
				});

				const intent = response?.data?.login_intent;
				// No intent means this user may still use password login
				if(!intent) {
					return false;
				}

				if(intent.status === 'enhanced_auth_required') {
					this.navigateTo('/oauth/start?email=' + encodeURIComponent(this.email) +
						'&redirect=' + encodeURIComponent(this.redirectUrl));
					return true;
				}

				if(intent.status === 'regional_redirect' && intent.redirect_url) {
					// The other region runs its own login, and we have no password to
					// forward from the email step, so hand the user off with just their
					// email - PLIC already puts it on the redirect url
					this.navigateTo(intent.redirect_url);
					return true;
				}
			} catch(e) {
				// eslint-disable-next-line
				console.error('Failed to lookup login intent: ', e);
			}

			return false;
		},
		editUsername() {
			this.step = 1;
			this.errorMessage = null;
			this.capsLockMessage = null;
			this.$v.password.$reset();
			this.$nextTick(() => {
				const emailField = this.$refs.email;
				if(emailField) {
					emailField.$el.querySelector('input').focus();
				}
			});
		},
		checkCapsLock(e) {
			// Not actually typing
			if(!e.key) {
				return;
			}

			let isCapsLockOn = e.getModifierState && e.getModifierState('CapsLock');
			if(isCapsLockOn) {
				this.capsLockMessage = 'You are typing your password with the caps lock on!';
			} else {
				this.capsLockMessage = null;
			}
		},
		async signIn() {
			this.errorMessage = null;
			this.$v.$touch();

			if(this.$v.$error) {
				return;
			}

			this.loading = true;

			// Regional migrations and enhanced sign-in are both branched on at the
			// email step now, in handleLoginIntent

			client.post('/ajax/login.php', {
				username: this.email,
				password: this.password,
				rememberMe: true
			}).then((response) => {
				if(!response.headers['content-type'].includes('json')) {
					throw new Error('Invalid response: ', response);
				} else if(response.data.promptAccounts) {
					setupAxiosAuthToken(response.data.plicToken);
					this.plicSlug = response.data.plicSlug;
					this.accountPickerDialog = true;
				} else {
					if(response.data.url) {
						this.navigateTo('/' + response.url);
					} else {
						this.navigateTo(this.redirectUrl);
					}
				}
			}).catch((error) => {
				this.loading = false;

				// PLIC would not check this password because the user is opted in to
				// enhanced sign-in. Send them through Auth0 rather than blaming their
				// password - we only get here when the email step could not branch, so
				// this is the safety net for cached bundles and mid-login opt-ins.
				const enhancedAuthStartUrl = error.response?.data?.enhancedAuthStartUrl;
				if(enhancedAuthStartUrl) {
					this.loading = true;
					this.navigateTo(enhancedAuthStartUrl);
					return;
				}

				this.errorMessage = 'The username/password combination you entered is incorrect.';
				try {
					if(error.response && error.response.data && error.response.data.reason) {
						let reason = error.response.data.reason;
						if(reason != 'Plic error' && reason != 'Authentication error' && reason != 'Error from PLIC') {
							this.errorMessage = reason;
						}
					}
				} catch(e) {
					// eslint-disable-next-line
					console.error('Failed to login: ', e, error);
				}
			});
		},
		// Enhanced sign-in comes back as a redirect, so there is no login response to
		// read the multiple-account prompt out of. The session already exists at this
		// point, so ask for the same values login.php would have returned.
		async openAccountPickerForSession() {
			this.loading = true;

			try {
				const response = await client.get('/ajax/loginState.php');

				if(response.data.promptAccounts) {
					setupAxiosAuthToken(response.data.plicToken);
					this.plicSlug = response.data.plicSlug;
					this.accountPickerDialog = true;
					return;
				}

				// Signed in with only one account after all - nothing to pick
				this.navigateTo(this.redirectUrl);
			} catch(e) {
				// eslint-disable-next-line
				console.error('Failed to load login state: ', e);

				this.loading = false;
				this.errorMessage = 'We could not finish signing you in. Please try again.';
			}
		},
		onError(errorMessage) {
			this.loading = false;
			this.errorMessage = errorMessage;
			this.accountPickerDialog = false;
		},
		// Single seam for leaving the page, so tests can assert where a login branch
		// sends the browser without the test run actually navigating away
		navigateTo(url) {
			window.location = url;
		}
	},
	mounted() {
		this.$refs.email.$el.querySelector('input').focus();

		// Two flows hand us an address in the url. A regional handoff - PLIC sending a
		// migrated user to their home region from the email step of another site -
		// arrives with ?email and nothing else. Our own failure bounces (an Auth0 sign
		// in that could not complete) use ?username paired with a warning to show. Only
		// the handoff should continue on its own; the bounce has to stop and be read.
		const handoffEmail = this.$route?.query?.email;
		const prefilledEmail = handoffEmail || this.$route?.query?.username;
		if(prefilledEmail) {
			this.email = prefilledEmail;
		}
		if(this.$route?.query?.warningMessage) {
			this.errorMessage = this.$route.query.warningMessage;
		}

		this.redirectUrl = sanitizeRedirect(this.$route?.query?.redirect);

		// Set by the enhanced sign-in callback when the user has more than one account
		if(this.$route?.query?.accounts) {
			this.openAccountPickerForSession();
		} else if(handoffEmail && !this.$route?.query?.warningMessage) {
			// The user already committed to signing in back on the other site, so pick
			// the sign-in up where they left off instead of making them press Next
			// again: this runs the same login-intent lookup and sends an enhanced-auth
			// user straight on to Auth0.
			this.goToPasswordStep();
		}
	},
    components: {
		AccountPickerDialog
	}
}
</script>

<style scoped>
.wrapper {
	display: flex;
	flex-direction: column;
	justify-content: center;
}
.app-logo {
	width: 50%;
	margin-left: auto;
	margin-right: auto;
	margin-bottom: 2em;
}

.bottom-message {
	margin-top: 1em;
}

.loading {
	min-height: 8em;
}

.policies {
	margin-top: 1em;
	display: block;
	display: flex;
	align-items: center;
	justify-content: center;
}

.forgot-password-button {
	font-size: 16px;
	color: var(--color-zinc-500);
}

#loginButton,
#nextButton {
	width: 100%;
}

.card-content {
	padding: 24px;
	/* Reserves enough vertical space for the two-row password step so that
	the card does not visually "jiggle" when switching between steps. */
	min-height: 232px;
	box-sizing: border-box;
}

.username-display {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	margin-bottom: 16px;
	border-bottom: 1px solid var(--color-zinc-200, #e4e4e7);
}

.username-display-text {
	font-size: 16px;
	color: var(--color-zinc-900, #18181b);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.username-edit {
	font-size: 14px;
	color: var(--v-primary-base, #1976d2);
	cursor: pointer;
	margin-left: 16px;
}

.username-edit:hover {
	text-decoration: underline;
}

.forgot-password-button {
	margin-top: 2em;
}

.error-message {
	margin-top: 1em;
	color: var(--v-error-base);
}
</style>